import React, {useState, useCallback, useEffect} from "react";
import PropTypes from "prop-types";
import ArgoColorInputHook from "./ArgoColorInputHook";
import ArgoTextDisplayCustomHook from "./ArgoTextDisplayCustomHook";
import ArgoTextFieldHook from "./ArgoTextFieldHook";
import ArgoTextFieldSelectHook from "./ArgoTextFieldSelectHook";
import * as TEMPLATE from "../../constants/templateConstants";
import * as icon from "../../constants/iconConstants";
import * as portalShared from "../../portal/shared/metadataConstantsAndUtilities"


const defaultOrCustomList = [
    {value: "default", text: "Default"},
    {value: "custom", text: "Custom"}
];

const ArgoCustomLikertInputStylingHook = (props) => {
    const { 
        template, 
        customMetadataTag, 
        templateUpdateFunction, 
        depth, 
        inputType,
        templateType,
        propsObj
    } = props;

    const [customObj, setCustomObj] = useState(JSON.parse(template.metadata[customMetadataTag]));

    useEffect(() => {
        setCustomObj(JSON.parse(template.metadata[customMetadataTag]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[props]);

    const setMetadataValue = useCallback((customObj) => {

        const clonedTemplate = portalShared.cloneObj(template);
        clonedTemplate.metadata[customMetadataTag] = JSON.stringify(customObj);
        templateUpdateFunction(clonedTemplate, depth);
    }, [template, customMetadataTag, templateUpdateFunction, depth]);

    const onSelectChange = useCallback((event, fieldName) => {
        const tempCustomObj = portalShared.cloneObj(customObj);
        tempCustomObj.likertObj[fieldName] = event.target.value;
        setCustomObj(tempCustomObj);
        setMetadataValue(tempCustomObj);
    }, [customObj, setMetadataValue]);

    const onTextChange = useCallback((event, fieldName) => {
        const tempCustomObj = portalShared.cloneObj(customObj);
        tempCustomObj.likertObj[fieldName] = event.target.value;
        setCustomObj(tempCustomObj);
        setMetadataValue(tempCustomObj);
    }, [customObj, setMetadataValue]);

    const setColor = useCallback((fieldName, colorObj) => {
        const tempCustomObj = portalShared.cloneObj(customObj);
        tempCustomObj.likertObj[fieldName].colorType = colorObj.colorType;
        tempCustomObj.likertObj[fieldName].colorIndex = colorObj.colorIndex;
        tempCustomObj.likertObj[fieldName].colorEntry = colorObj.colorEntry;
        tempCustomObj.likertObj[fieldName].color = colorObj.color;
        tempCustomObj.likertObj[fieldName].opacity = colorObj.opacity;
        tempCustomObj.likertObj[fieldName].opacityEntry = colorObj.opacityEntry;
        setCustomObj(tempCustomObj);
        setMetadataValue(tempCustomObj);
    }, [customObj, setMetadataValue]);

    const displayColorEntries = (name, label, namePrefix1, namePrefix2, index, opacityFlag = false) => {
        return <div style={{display: "flex", alignItems: "left", width: "100%"}}>
            <div>{getColorEntry(name, namePrefix1, label, index, 0, opacityFlag)}</div>
            <div>{getColorEntry(name, namePrefix2, "Hover " + label, index, 0, opacityFlag)}</div>
        </div>
    };

    const getColorEntry = (name, prefixLowerCase, label, index, prefixIndex, opacityFlag) => {
        const fieldName = prefixLowerCase + name;
        return <div style={{display: "flex", alignItems: "left", width: "100%"}}>
            <ArgoColorInputHook
                mode="questionSingleSelect"
                colorLabel={label + " " + name}
                propsFieldName={fieldName}
                index={index + prefixIndex}
                initToObj={customObj.likertObj[fieldName]}
                setColor={setColor}
                opacityFlag={opacityFlag}
                selectDefaultWidth={200}
            />
            <div style={{width: "80px"}}>&nbsp;</div>
        </div>
    };

    const displayTextEntries = (name, labelPrefix, label, namePrefix1, namePrefix2, index) => {
        return <div style={{display: "flex", alignItems: "left", width: "100%"}}>
            <div>{getTextEntry(name, namePrefix1, labelPrefix + " " + label, index, 1)}</div>
            <div style={{width: "20px"}}>&nbsp;</div>
            <div>{getTextEntry(name, namePrefix2, labelPrefix + " Hover " + label, index, 2)}</div>
        </div>
    };

    const getTextEntry = (name, namePrefix, label, index, prefixIndex) => {
        const fieldName = namePrefix + name;

        if (name === "FontSize" || name === "BorderWidth") {
            return <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                <ArgoTextFieldHook
                    fileName="ArgoCustomLikertInputStyling"
                    fieldName={fieldName}
                    index={index + prefixIndex}
                    type="number"
                    label={label}
                    value={customObj.likertObj[fieldName]}
                    onChange={onTextChange}
                    width={200}
                />
            </div>
        } else {
            // Font Style or Font Weight
            const selectList = (name === "FontStyle") ? portalShared.fontStyleList : portalShared.fontWeightList;
            return <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                <ArgoTextFieldSelectHook
                    fileName="ArgoCustomLikertInputStyling"
                    fieldName={fieldName}
                    label={label}
                    value={customObj.likertObj[fieldName]}
                    onChange={onSelectChange}
                    width="200px"
                    menuItems={selectList}
                />
            </div>
        }
    };

    const isSquareOrCircle = (inputType === icon.TYPE_SQUARE || inputType === icon.TYPE_CIRCLE);

    return (
        <div style={{width: "100%", backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX, paddingLeft: "10px", paddingTop: "10px"}}>
            <ArgoTextFieldSelectHook
                fileName="ArgoCustomLikertInputStyling"
                fieldName="customLikertInputStyling"
                label="Likert Input Style"
                value={customObj.likertObj.customLikertInputStyling}
                onChange={onSelectChange}
                width="150px"
                menuItems={defaultOrCustomList}
            />

            {customObj.likertObj.customLikertInputStyling === "custom" ?
                <div style={{paddingLeft: "20px"}}>
                    {!isSquareOrCircle ?
                        <React.Fragment>
                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayColorEntries("Icon", "Unselected", "unselected", "hoverUnselected", 0, true)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayColorEntries("Icon", "Selected", "selected", "hoverSelected", 1, true)}</div>
                            </div>
                        </React.Fragment> : ""
                    }

                    {isSquareOrCircle ?
                        <React.Fragment>
                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayColorEntries("Border", "Unselected", "unselected", "hoverUnselected", 0)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayColorEntries("Border", "Selected", "selected", "hoverSelected", 1)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayColorEntries("Background", "Unselected", "unselected", "hoverUnselected", 2)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayColorEntries("Background", "Selected", "selected", "hoverSelected", 3)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayColorEntries("Text", "Unselected", "unselected", "hoverUnselected", 4)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayColorEntries("Text", "Selected", "selected", "hoverSelected", 5)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayTextEntries("BorderWidth", "Unselected", "Border Width", "unselected", "hoverUnselected", 6)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayTextEntries("BorderWidth", "Selected", "Border Width", "selected", "hoverSelected", 7)}</div>
                            </div>
                        </React.Fragment> : ""
                    }

                    {/*<div>Text Styling, Font Family, Font Size, Font Style, Font Weight for CIRCLES and SQUARES</div>*/}

                    {isSquareOrCircle ?
                        <div>
                            <ArgoTextDisplayCustomHook
                                templateType={templateType}
                                textEntryType={TEMPLATE.TEXT_TYPE.QUESTION.NAME}
                                fieldName="unselectedFontFamily"
                                fieldTextObjName="unselectedFontFamily"
                                initToObj={customObj.likertObj}
                                displayLabel="test"
                                metadataPropsObj={Object.assign({metadataTag: customMetadataTag}, propsObj)}
                                indent={false}
                                showDisplay={false}
                                showSize={false}
                                showStyle={false}
                                showWeight={false}
                                showColor={false}
                                showOpacity={false}
                            />

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayTextEntries("FontSize", "Unselected", "Font Size", "unselected", "hoverUnselected", 6)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayTextEntries("FontSize", "Selected", "Font Size", "selected", "hoverSelected", 7)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayTextEntries("FontStyle", "Unselected", "Font Style", "unselected", "hoverUnselected", 8)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayTextEntries("FontStyle", "Selected", "Font Style", "selected", "hoverSelected", 9)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayTextEntries("FontWeight", "Unselected", "Font Weight", "unselected", "hoverUnselected", 10)}</div>
                            </div>

                            <div>&nbsp;</div>

                            <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                                <div>{displayTextEntries("FontWeight", "Selected", "Font Weight", "selected", "hoverSelected", 11)}</div>
                            </div>
                        </div> : ""
                    }
                </div> : ""
            }

            <div>&nbsp;</div>
        </div>
    );
};

ArgoCustomLikertInputStylingHook.defaultProps = {};

ArgoCustomLikertInputStylingHook.propTypes = {
    templateType: PropTypes.string,
    template: PropTypes.object,
    depth: PropTypes.number,
    customMetadataTag: PropTypes.string,
    templateUpdateFunction: PropTypes.func,
    inputType: PropTypes.string
};

export default ArgoCustomLikertInputStylingHook;

